/**
 * S3 · Verify + Consent (B3 Group 2)
 *
 * Order (atomicity — approved plan Fix 1):
 *   1) Collect email + explicit biometric + terms consent (ticks).
 *   2) supabase.auth.signInWithOtp({ email }) → email OTP.
 *   3) On OTP verify → user is authed with confirmed email.
 *   4) BEFORE graduating, record consents on GUEST session:
 *        record_biometric_consent → attach_selfie_to_guest_session → record_terms_consent.
 *   5) Invoke graduate-consumer edge fn (server-side carry_guest_consents_to_user copies
 *      biometric + terms rows from guest scope to user scope).
 *   6) Route to S4.
 *
 * Nothing on this screen calls generate-avatar — the B4 gate is verified on S4.
 *
 * AGE AFFIRMATION (one bit) sits here with biometric and terms. It is recorded
 * server-side the moment it is ticked (`record_age_affirmation`), carried to the
 * user scope at graduation, and re-checked by `generate-avatar` before any
 * processing begins. Capture upstream is deliberately NOT gated: holding a selfie
 * is not processing it.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  readGuestToken,
  readSelfiePath,
  clearGuestToken,
  recordFunnelEvent,
  recordFunnelEventDurably,
} from "@/lib/consumer-session";
import { recordSignal } from "@/lib/funnel-signals";
import { useConsumerFlow } from "../ConsumerFlow";
import { OTP_LENGTH, OTP_LENGTH_LABEL, isCompleteOtp, normalizeOtpInput } from "@/lib/otp";
import { verifyOtpWithTypeFallback } from "@/lib/otp-verification";
import {
  FunnelPage,
  FunnelContainer,
  FunnelHeader,
  Display,
  Lede,
  StickyBar,
  PrimaryButton,
  inputClass,
  TickIcon,
} from "@/components/consumer/chrome";
import { OtpInput } from "@/components/consumer/OtpInput";
import { cn } from "@/lib/utils";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S3_SECTION, S3_DEFAULTS } from "@/content/screens/s3";
import { AgeAffirmationCard, AgeRefusal, useAgeAffirmation } from "@/components/consumer/AgeAffirmation";
import { getAgeAffirmationState } from "@/services/age-gate";
import { recordMarketingConsent, requestMarketingSync } from "@/services/marketing-consent";
import { LegalDocLinks } from "@/components/consumer/LegalDocLinks";
import type { LegalDocKey } from "@/lib/legal-consent";

/** Stable references so the link loaders don't refetch on every render. */
const BIOMETRIC_DOCS: readonly LegalDocKey[] = ["biometric"];
const TERMS_DOCS: readonly LegalDocKey[] = ["terms", "privacy"];

/**
 * F3 · one purpose per step. Email, then the consents, then the code — three
 * screens over ONE component, so nothing about the atomic order below changes:
 * consents are still recorded on the guest session after verification and
 * before graduation.
 */
type Phase = "email" | "consent" | "code" | "consenting" | "graduating" | "done" | "error";

function ConsentCard({
  checked,
  onToggle,
  testId,
  title,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  testId: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      role="checkbox"
      tabIndex={0}
      aria-checked={checked}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onToggle();
        }
      }}
      data-testid={testId}
      className={cn(
        "mb-3 flex cursor-pointer gap-3.5 rounded-[18px] border bg-white p-[18px] transition-colors",
        checked ? "border-grape" : "border-foreground/[0.12] hover:border-foreground/30",
      )}
    >
      <div
        className={cn(
          "mt-0.5 grid h-[26px] w-[26px] flex-none place-items-center rounded-[8px] border-2",
          checked ? "border-grape bg-grape" : "border-foreground/[0.28] bg-white",
        )}
      >
        {checked ? <TickIcon className="h-4 w-4" /> : null}
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <span className="text-[16px] font-semibold leading-[1.35]">{title}</span>
        {children}
      </div>
    </div>
  );
}

export default function S3VerifyConsent() {
  const { t } = useSiteContent(S3_SECTION, S3_DEFAULTS);
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [biometric, setBiometric] = useState(false);
  const [terms, setTerms] = useState(false);
  /**
   * Marketing is OPTIONAL and starts UNTICKED, always. A pre-ticked marketing
   * box is not consent under GDPR, so it is never defaulted on, never bundled
   * into the required ticks, and never blocks the code request.
   */
  const [marketing, setMarketing] = useState(false);
  const age = useAgeAffirmation(program.slug, program.program_id);
  const ageOk = age.status === "passed";
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* A code sent and never typed is a drop-off CAUSE: the arrival and the
     consent completion both exist, so without this the gap between them has no
     explanation. Recorded when the screen is left, because until then the
     customer may still be fetching the email. */
  const codeRequestedRef = useRef(false);
  const codeEnteredRef = useRef(false);
  useEffect(() => () => {
    if (codeRequestedRef.current && !codeEnteredRef.current) {
      recordSignal("code_not_entered");
    }
  }, []);

  /**
   * P4.4 · OTP request hygiene.
   *
   * GoTrue invalidates the previous code every time a new one is issued, and it
   * holds a ~60s per-address cooldown. The old screen had three sharp edges that
   * combined into "token has expired or is invalid" on a code that had just
   * arrived, followed by a silent 27-second lockout:
   *   1. A verify failure dropped `phase` back to "collect", so the natural next
   *      action was pressing "Send code" again — issuing a NEW code and killing
   *      the one in the user's hand.
   *   2. Nothing stopped a second request inside the cooldown, so the raw 429
   *      ("only after 27 seconds") surfaced as an unexplained dead end.
   *   3. Nothing told the user that a resend invalidates the earlier email, so
   *      typing the FIRST code after a resend looked like a server bug.
   * Fixes: stay on the code step, cooldown the resend with a live countdown, and
   * say plainly which email to use.
   */
  const RESEND_COOLDOWN_SECONDS = 60;
  const [cooldown, setCooldown] = useState(0);
  const sendingRef = useRef(false);
  /**
   * How many codes THIS screen asked for. It is the only thing we know that the
   * provider's one-size error does not tell us: with a single request, nothing
   * cancelled anything, so the failure message must not say it did.
   */
  const requestCountRef = useRef(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  /**
   * Belt to the S1/S2 routing fix: S3 exists to IDENTIFY someone. If a session
   * is already live when this screen mounts (e.g. a stale link or a back/forward
   * landing), there is nothing to identify — go on to generating instead of
   * asking a verified person for their email again.
   */
  const [identityChecked, setIdentityChecked] = useState(false);
  /**
   * ...but the bypass must not skip an OUTSTANDING gate. A signed-in person whose
   * user scope holds no `age` consent was sent to generating, refused there with
   * AGE_AFFIRMATION_REQUIRED, and routed back here — which bounced them forward
   * again. The remedy screen has to be reachable, so when the gate is open at
   * this scope the affirmation OWNS the screen: no email, no OTP, just the tick.
   */
  const [signedInGateOnly, setSignedInGateOnly] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!data?.user) { setIdentityChecked(true); return; }
      const state = await getAgeAffirmationState(null);
      if (cancelled) return;
      // Fails closed: an unreadable answer shows the tick rather than assuming it.
      if (state?.affirmed) navigate("../generating", { replace: true });
      else { setSignedInGateOnly(true); setIdentityChecked(true); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const guestToken = readGuestToken(program.slug);
  const selfiePath = readSelfiePath(program.slug);
  const missingPrereq = !guestToken || !selfiePath;

  // All THREE affirmations, or no code and no processing.
  const canSendCode = email.trim() && biometric && terms && ageOk && !busy && !missingPrereq;

  /**
   * Single funnel for every code request. Re-entrant calls are dropped rather
   * than sent: a duplicate request is exactly what invalidates the code the
   * user is about to type.
   */
  const sendCode = async () => {
    if (sendingRef.current || busy || cooldown > 0) return;
    sendingRef.current = true;
    setError(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { shouldCreateUser: true },
      });
      if (error) throw error;
      setOtp("");
      codeRequestedRef.current = true;
      requestCountRef.current += 1;
      setPhase("code");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e: any) {
      // GoTrue reports the remaining cooldown in prose ("...after 27 seconds").
      // Turn that into a countdown the UI can own instead of a dead end.
      const secs = Number(/after (\d+) second/.exec(e?.message || "")?.[1] ?? 0);
      if (secs > 0) {
        setCooldown(secs);
        setError(
          `A code was just sent to ${email.trim()}. Use the code in that email — ` +
          `you can request a new one in ${secs}s.`,
        );
        // Keep them on the code step: their valid code is already in the inbox.
        setPhase((prev) => (prev === "code" ? prev : "code"));
      } else {
        setError(e?.message || t("s3.errors.sendFailed"));
      }
    } finally {
      setBusy(false);
      sendingRef.current = false;
    }
  };

  /**
   * SUBMIT THE VALUE THAT WAS JUST WRITTEN, never a re-read of state.
   *
   * The auto-advance fix let the boxes accept a whole autofilled string. That
   * was right, and it introduced a race: the boxes fired "complete" in the same
   * tick as the change, so this function ran BEFORE React had stored the digits
   * and asked the provider with an EMPTY token. The provider answered
   * "Verify requires either a token or a token hash" — a different failure from
   * a rejected code, and indistinguishable from one in our records.
   *
   * So the completed code is passed in as an argument and used directly. A
   * submit with the wrong length never reaches the provider at all.
   */
  const submittedCodesRef = useRef<string[]>([]);
  const verifyCode = async (submitted?: string) => {
    const code = normalizeOtpInput(submitted ?? otp).trim();
    // Keep the boxes and the request in agreement — the value we sent IS shown.
    if (code !== otp) setOtp(code);

    /**
     * WHAT WAS SUBMITTED, recorded so a failure is diagnosable in ONE attempt.
     *
     * Three diagnoses in a row (stale autofill, token-type mismatch, expiry)
     * were each reasonable and NONE was checkable, because nothing recorded
     * what we actually sent. Length and repeat-of-an-earlier-code answer it.
     * The digits themselves are never recorded, sent, or logged.
     */
    const repeated = submittedCodesRef.current.includes(code);
    if (code) submittedCodesRef.current.push(code);
    if (!isCompleteOtp(code)) {
      void recordFunnelEventDurably(program.slug, "code_submitted", program.program_id, {
        submitted_length: code.length,
        expected_length: OTP_LENGTH,
        repeated_earlier_code: repeated,
        requests_this_screen: requestCountRef.current,
        source: submitted === undefined ? "button" : "autofill_or_complete",
        provider_attempt: false,
      });
      // An empty or short submit is OUR bug, not the customer's code.
      setError(`Enter all ${OTP_LENGTH} digits from the email, then try again.`);
      return;
    }

    // "Requested but never ENTERED" — typing it counts even if it turns out to
    // be wrong; a wrong code is a different problem from an unopened email.
    codeEnteredRef.current = true;
    setError(null);
    setBusy(true);
    try {
      /**
       * TWO TOKEN TYPES, ONE CODE BOX.
       *
       * The provider stores the emailed code under DIFFERENT token types
       * depending on whether the address already has an account:
       *   - new address   -> confirmation_token, checked by type "email"
       *   - existing user -> recovery_token,     checked by type "magiclink"
       *
       * Asking with the wrong type returns the SAME "invalid or expired"
       * error as a stale code, so an existing customer could never sign in and
       * the screen blamed the code. We ask "email" first and, only when the
       * provider says not-found/expired, ask again as "magiclink" with the same
       * code. A genuinely wrong or timed-out code still fails both.
       */
      let providerAttempt = 0;
      const { data, error } = await verifyOtpWithTypeFallback(code, async (type, guardedToken) => {
        providerAttempt += 1;
        await recordFunnelEventDurably(program.slug, "code_submitted", program.program_id, {
          submitted_length: guardedToken.length,
          expected_length: OTP_LENGTH,
          repeated_earlier_code: repeated,
          requests_this_screen: requestCountRef.current,
          source: submitted === undefined ? "button" : "autofill_or_complete",
          provider_attempt: true,
          provider_attempt_number: providerAttempt,
          provider_token_type: type,
        });
        return supabase.auth.verifyOtp({ email: email.trim(), token: guardedToken, type });
      });
      if (error) throw error;
      if (!data.session) throw new Error("Verification did not return a session.");

      // Record consents on guest session BEFORE graduation.
      setPhase("consenting");
      const bio = await supabase.rpc("record_biometric_consent", { p_token: guestToken });
      if (bio.error) throw bio.error;
      const bind = await supabase.rpc("attach_selfie_to_guest_session", {
        p_token: guestToken,
        p_selfie_path: selfiePath,
      });
      if (bind.error) throw bind.error;
      const t = await supabase.rpc("record_terms_consent", {
        p_token: guestToken,
        p_policy_version: 1,
      });
      if (t.error) throw t.error;

      /**
       * Marketing: recorded ONLY when explicitly ticked. An unticked box writes
       * nothing at all — no consent row, so no Resend contact either. A failure
       * here must not cost somebody their card, so it is logged, not thrown.
       */
      if (marketing) {
        const mk = await recordMarketingConsent("granted", guestToken);
        if (mk.error) console.error("[s3] marketing consent write failed", mk.error.message);
      }

      // Graduate — server-side carry copies consents to user scope.
      setPhase("graduating");
      const grad = await supabase.functions.invoke("graduate-consumer", {
        body: { guest_token: guestToken },
      });
      if (grad.error) throw grad.error;
      if (grad.data && (grad.data as any).ok === false) {
        throw new Error((grad.data as any).error || "graduation_failed");
      }

      // The carry produced the user-scope marketing row; nudge the mirror.
      if (marketing) void requestMarketingSync();


      recordFunnelEvent(program.slug, "consented", program.program_id);
      recordFunnelEvent(program.slug, "account_created", program.program_id);
      // Guest token is no longer useful — future calls run as the authed user.
      clearGuestToken(program.slug);
      setPhase("done");
      navigate("../generating");
    } catch (e: any) {
      const msg: string = e?.message || "";
      /**
       * WHAT ACTUALLY HAPPENED, not one cause for three failures.
       *
       * GoTrue answers "token has expired or is invalid" (`otp_expired`) for ALL
       * of: timed out, cancelled by a newer code, and simply not the right code.
       * Asserting supersession for all three told a customer who requested ONE
       * code that they had requested two — the same defect as "we couldn't
       * create your face just now" naming one reason for an unrelated gate.
       *
       * We cannot learn the cause from the provider, so we say only what WE
       * know: how many codes we asked for on this screen. One request means
       * nothing cancelled anything, so cancellation is not mentioned. We do not
       * claim a timeout either — the validity window is not readable from here,
       * so a timing claim would be invented.
       */
      if (/expired|invalid/i.test(msg)) {
        setError(
          requestCountRef.current > 1
            ? "Only the code in the newest email works — requesting another code cancels the earlier one. " +
              "Open the most recent email from us and enter that code" +
              (cooldown > 0 ? `, or request a fresh one in ${cooldown}s.` : ".")
            : `That code wasn't accepted. Check the newest email from us — the code is ${OTP_LENGTH} digits — and enter it again` +
              (cooldown > 0 ? `, or request a fresh one in ${cooldown}s.` : "."),
        );
      } else {
        setError(msg || t("s3.errors.verifyFailed"));
      }

      // Stay on the code step with the email intact: nothing already captured
      // (style, selfie, consents) needs re-doing.
      setPhase("code");
    } finally {
      setBusy(false);
    }
  };

  const emailValid = /\S+@\S+\.\S+/.test(email.trim());
  const isEmailStep = phase === "email" || phase === "error";
  const isConsentStep = phase === "consent";

  // Refusal is terminal and owns the screen outright.
  if (age.status === "blocked") return <AgeRefusal />;

  // Don't flash the email form at someone who turns out to be signed in.
  if (!identityChecked) {
    return (
      <FunnelPage testId="s3-verify">
        <FunnelContainer size="form">
          <p className="py-12 text-[15px] text-foreground/60" data-testid="s3-verify-checking">Checking your session…</p>
        </FunnelContainer>
      </FunnelPage>
    );
  }

  // Signed in, identity already established — the only thing missing is the tick.
  if (signedInGateOnly) {
    return (
      <FunnelPage testId="s3-verify">
        <FunnelContainer size="form">
          <FunnelHeader backTo="../selfie" backLabel={t("s3.header.back")} step={t("s3.header.step")} />
          <div data-testid="s3-verify-age-only">
            <h1 className="font-brand mb-3 max-w-[18ch] text-[32px] font-semibold leading-[1.03] tracking-[-0.03em] text-balance md:text-[clamp(32px,4.6vw,54px)]">
              One last confirmation
            </h1>
            <Lede className="mb-6 max-w-[44ch]">
              We just need you to confirm your age before we make your face. Nothing else — you're already signed in.
            </Lede>
            <AgeAffirmationCard
              affirmed={ageOk}
              threshold={age.threshold}
              busy={age.busy}
              error={age.error}
              onAffirm={age.affirm}
              onDecline={age.decline}
            />
            <PrimaryButton
              type="button"
              disabled={!ageOk}
              onClick={() => navigate("../generating", { replace: true })}
              data-testid="s3-verify-age-only-continue"
            >
              Continue
            </PrimaryButton>
          </div>
        </FunnelContainer>
      </FunnelPage>
    );
  }

  return (
    <FunnelPage testId="s3-verify">
      <FunnelContainer size="form">
        {/* G4 · ONE back affordance, and it steps back through the split. */}
        <FunnelHeader
          {...(isEmailStep
            ? { backTo: "../selfie" }
            : { onBack: () => { setError(null); setPhase(isConsentStep ? "email" : "consent"); } })}
          backLabel={t("s3.header.back")}
          step={t("s3.header.step")}
        />

        {missingPrereq && (
          <p className="mb-4 text-[14px] font-medium text-[#EC1314]" data-testid="s3-verify-missing-prereq">
            {t("s3.errors.missingPrereq")}
          </p>
        )}

        {isEmailStep ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (emailValid && !missingPrereq) { setError(null); setPhase("consent"); }
            }}
            data-testid="s3-verify-collect"
          >
            <h1 className="font-brand mb-3 max-w-[18ch] text-[32px] font-semibold leading-[1.03] tracking-[-0.03em] text-balance md:text-[clamp(32px,4.6vw,54px)]" data-testid="s3-verify-title">{t("s3.collect.title")}</h1>
            <Lede className="mb-6 max-w-[44ch]">
              {t("s3.collect.lede")}
            </Lede>

            <label className="mb-6 block">
              <span className="mb-2 block text-[14px] font-semibold">{t("s3.collect.emailLabel")}</span>
              <input
                type="email"
                required
                inputMode="email"
                autoComplete="email"
                placeholder={t("s3.collect.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="s3-verify-email"
                className={inputClass}
              />
              <p className="mt-2.5 text-[14px] leading-[1.5] text-foreground/60">
                {t("s3.collect.emailHelp")}
              </p>
            </label>

            {error && (
              <p className="mb-2 text-[14px] font-medium text-[#EC1314]" data-testid="s3-verify-error">
                {error}
              </p>
            )}

            <StickyBar>
              <PrimaryButton type="submit" disabled={!emailValid || missingPrereq} data-testid="s3-verify-email-continue">
                {t("s3.email.continue")}
              </PrimaryButton>
            </StickyBar>
          </form>
        ) : isConsentStep ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSendCode) sendCode();
            }}
            data-testid="s3-verify-consent"
          >
            <h1 className="font-brand mb-3 max-w-[18ch] text-[32px] font-semibold leading-[1.03] tracking-[-0.03em] text-balance md:text-[clamp(32px,4.6vw,54px)]" data-testid="s3-verify-title">{t("s3.consent.stepTitle")}</h1>
            <Lede className="mb-6 max-w-[44ch]">{t("s3.consent.stepLede")}</Lede>

            {/*
              THREE separately recorded consents, THREE affirmations. The wording
              is deliberately distinct — the age clause used to sit inside the
              terms tick, which made two collections read as one repeated.
            */}
            <ConsentCard checked={biometric} onToggle={() => setBiometric((v) => !v)} testId="s3-verify-biometric" title={t("s3.consent.biometricTitle")}>
              <span className="text-[14px] leading-[1.55] text-foreground/70">
                {t("s3.consent.biometricBody")}
              </span>
              {/* L3 · the exact register version behind this tick, readable now. */}
              <LegalDocLinks docKeys={BIOMETRIC_DOCS} testId="s3-verify-biometric-links" />
            </ConsentCard>

            <ConsentCard checked={terms} onToggle={() => setTerms((v) => !v)} testId="s3-verify-terms" title={t("s3.consent.termsTitle")}>
              <span className="text-[14px] leading-[1.55] text-foreground/70">
                {t("s3.consent.termsBody")}
              </span>
              <LegalDocLinks docKeys={TERMS_DOCS} testId="s3-verify-terms-links" />
            </ConsentCard>


            <AgeAffirmationCard
              affirmed={ageOk}
              threshold={age.threshold}
              busy={age.busy}
              error={age.error}
              onAffirm={age.affirm}
              onDecline={age.decline}
            />

            {/* OPTIONAL — unticked by default, and it never gates anything. */}
            <ConsentCard
              checked={marketing}
              onToggle={() => setMarketing((v) => !v)}
              testId="s3-verify-marketing"
              title="Email me Cardmoji news (optional)"
            >
              <span className="text-[14px] leading-[1.55] text-foreground/70">
                New styles, features and offers. Optional — leave this unticked and
                you'll only ever hear from us about your own order. You can change
                your mind any time, in My Account or from any email we send.
              </span>
            </ConsentCard>

            {/* hidden accessible checkboxes preserved for testids */}
            <input type="checkbox" className="sr-only" checked={biometric} onChange={(e) => setBiometric(e.target.checked)} data-testid="s3-verify-biometric-label" readOnly />
            <input type="checkbox" className="sr-only" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="s3-verify-terms-label" readOnly />
            <input type="checkbox" className="sr-only" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="s3-verify-marketing-label" readOnly />


            {error && (
              <p className="mb-2 text-[14px] font-medium text-[#EC1314]" data-testid="s3-verify-error">
                {error}
              </p>
            )}

            <StickyBar>
              <span className="w-full text-[14px] leading-[1.4] text-foreground/60">
                {t("s3.collect.stickyNote").replace("{otpLength}", String(OTP_LENGTH))}
              </span>
              <PrimaryButton type="submit" disabled={!canSendCode} data-testid="s3-verify-send-code">
                {busy ? t("s3.collect.sending") : t("s3.collect.sendCode")}
              </PrimaryButton>
            </StickyBar>
          </form>
        ) : (

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!busy) verifyCode(otp);
            }}
            data-testid="s3-verify-code-form"
          >
            <h1 className="font-brand mb-3 max-w-[18ch] text-[32px] font-semibold leading-[1.03] tracking-[-0.03em] text-balance md:text-[clamp(32px,4.6vw,54px)]" data-testid="s3-verify-title">{t("s3.code.title")}</h1>
            <Lede className="mb-2 max-w-[44ch]">
              {(() => {
                const [before, after] = t("s3.code.lede")
                  .replace("{otpLengthLabel}", OTP_LENGTH_LABEL)
                  .split("{email}");
                return (
                  <>
                    {before}
                    <strong className="font-semibold text-foreground">{email}</strong>
                    {after}
                  </>
                );
              })()}
            </Lede>
            {/*
              G4 · no "change email" control here — the back arrow steps to the
              consent screen and then to the email screen, which is the same
              route and the only backward affordance on the page.
              F5 · one code control, single-digit boxes, auto-advancing.
            */}
            <OtpInput
              value={normalizeOtpInput(otp)}
              onChange={setOtp}
              onComplete={(code) => { if (!busy) verifyCode(code); }}
              disabled={busy}
              autoFocus
              testId="s3-verify-otp"
            />


            <div className="mb-4 flex items-center gap-1.5 text-[15px] text-foreground/60">
              <span>{t("s3.code.resendPrompt")}</span>
              <button
                type="button"
                onClick={sendCode}
                disabled={busy || cooldown > 0}
                className="min-h-11 px-1 py-3 text-[15px] font-semibold text-grape hover:text-tangerine disabled:cursor-not-allowed disabled:text-foreground/40 disabled:hover:text-foreground/40"
                data-testid="s3-verify-resend"
              >
                {cooldown > 0 ? `${t("s3.code.resend")} (${cooldown}s)` : t("s3.code.resend")}
              </button>
            </div>
            <p className="mb-4 text-[13px] leading-[1.5] text-foreground/55" data-testid="s3-verify-resend-note">
              A new code cancels the previous one, so always use the latest email.
            </p>

            {phase === "consenting" && <p className="mb-2 text-[14px]" data-testid="s3-verify-recording">{t("s3.code.recording")}</p>}
            {phase === "graduating" && <p className="mb-2 text-[14px]" data-testid="s3-verify-graduating">{t("s3.code.graduating")}</p>}
            {error && (
              <p className="mb-2 text-[14px] font-medium text-[#EC1314]" data-testid="s3-verify-error">
                {error}
              </p>
            )}

            <StickyBar>
              <span className="w-full text-[14px] leading-[1.4] text-foreground/60" />
              <PrimaryButton type="submit" disabled={busy || !isCompleteOtp(otp)} data-testid="s3-verify-submit-otp">
                {busy ? t("s3.code.verifying") : t("s3.code.submit")}
              </PrimaryButton>
            </StickyBar>
          </form>
        )}
      </FunnelContainer>
    </FunnelPage>
  );
}
